/**
 * Typed setters/getters for every modulation target. The maps are exhaustive
 * over the ModulationTarget union — TypeScript catches any new target that
 * forgets a setter (see the `Record<ModulationTarget, ...>` types below).
 *
 * The contribution is added to the *base* preset value each frame, so we
 * need a getter (to read the base) and a setter (to write base + sum).
 */

import type { SeedParams } from '../../renderer/webgpu/passes/seedPass';
import type { WarpParams } from '../../renderer/webgpu/passes/feedbackWarpPass';
import type { ModulationTarget } from '../presets/presetTypes';

export type ParamBundle = {
  warp: WarpParams;
  seed: SeedParams;
};

type Setter = (b: ParamBundle, value: number) => void;
type Getter = (b: ParamBundle) => number;

export const TARGET_SETTERS: Record<ModulationTarget, Setter> = {
  'warp.baseZoom': (b, v) => {
    b.warp.baseZoom = v;
  },
  'warp.bassZoomGain': (b, v) => {
    b.warp.bassZoomGain = v;
  },
  'warp.rotation': (b, v) => {
    b.warp.rotation = v;
  },
  'warp.bassRotationGain': (b, v) => {
    b.warp.bassRotationGain = v;
  },
  'warp.ripple': (b, v) => {
    b.warp.ripple = v;
  },
  'warp.rippleFreq': (b, v) => {
    b.warp.rippleFreq = v;
  },
  // Brightness guard: decay near 1 causes runaway feedback gain
  // (gain ~= 1/(1-decay) at 60fps). Cap at 0.99 so worst-case steady-state
  // gain stays under ~100x even if a preset over-modulates upward.
  'warp.decay': (b, v) => {
    b.warp.decay = Math.max(0.9, Math.min(0.99, v));
  },
  'warp.chroma': (b, v) => {
    b.warp.chroma = v;
  },
  'warp.hueShift': (b, v) => {
    b.warp.hueShift = v;
  },
  'warp.beatPunch': (b, v) => {
    b.warp.beatPunch = v;
  },
  'warp.centerDriftAmp': (b, v) => {
    b.warp.centerDriftAmp = v;
  },
  'warp.centerDriftRate': (b, v) => {
    b.warp.centerDriftRate = v;
  },
  'seed.baseY': (b, v) => {
    b.seed.baseY = v;
  },
  'seed.gain': (b, v) => {
    b.seed.gain = v;
  },
  'seed.halfThick': (b, v) => {
    b.seed.halfThick = v;
  },
  // Brightness guard: seed.intensity > 1 injects more-than-full-color
  // energy into a feedback loop that already has tens of x of gain,
  // driving whiteout. Cap at 1.0 so peak modulation can't oversaturate.
  'seed.intensity': (b, v) => {
    b.seed.intensity = Math.max(0, Math.min(1.0, v));
  },
  'seed.color.r': (b, v) => {
    b.seed.color = [v, b.seed.color[1], b.seed.color[2]];
  },
  'seed.color.g': (b, v) => {
    b.seed.color = [b.seed.color[0], v, b.seed.color[2]];
  },
  'seed.color.b': (b, v) => {
    b.seed.color = [b.seed.color[0], b.seed.color[1], v];
  },
  'seed.edgeSharp': (b, v) => {
    b.seed.edgeSharp = v;
  },
};

export const TARGET_GETTERS: Record<ModulationTarget, Getter> = {
  'warp.baseZoom': (b) => b.warp.baseZoom,
  'warp.bassZoomGain': (b) => b.warp.bassZoomGain,
  'warp.rotation': (b) => b.warp.rotation,
  'warp.bassRotationGain': (b) => b.warp.bassRotationGain,
  'warp.ripple': (b) => b.warp.ripple,
  'warp.rippleFreq': (b) => b.warp.rippleFreq,
  'warp.decay': (b) => b.warp.decay,
  'warp.chroma': (b) => b.warp.chroma,
  'warp.hueShift': (b) => b.warp.hueShift,
  'warp.beatPunch': (b) => b.warp.beatPunch,
  'warp.centerDriftAmp': (b) => b.warp.centerDriftAmp,
  'warp.centerDriftRate': (b) => b.warp.centerDriftRate,
  'seed.baseY': (b) => b.seed.baseY,
  'seed.gain': (b) => b.seed.gain,
  'seed.halfThick': (b) => b.seed.halfThick,
  'seed.intensity': (b) => b.seed.intensity,
  'seed.color.r': (b) => b.seed.color[0],
  'seed.color.g': (b) => b.seed.color[1],
  'seed.color.b': (b) => b.seed.color[2],
  'seed.edgeSharp': (b) => b.seed.edgeSharp,
};
